import { TableColumn, TableRowSelectorDirective, LegacyTableModule } from 'remarketing-ui-components-lib';
import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormGroup, UntypedFormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MatSlideToggle, MatSlideToggleChange, MatSlideToggleModule } from '@angular/material/slide-toggle';
import { TranslocoService, TranslocoDirective } from '@jsverse/transloco';
import { Select, Store } from '@ngxs/store';
import {
  Configuration,
  ConfigurationState,
  FeeType,
  FinanceSource,
  FinanceSourceService,
  Organization,
  OrganizationProfile,
  OrganizationService,
  SaveOrganization
} from 'remarketing-angular-library';
import { Observable, Subject } from 'rxjs';
import { map, take, takeUntil } from 'rxjs/operators';
import { AnalyticsService } from 'smartauction-frontend-micro-library';
import { tenantClass } from 'src/profile-management/util/state-selectors';
import { HttpParams } from "@angular/common/http";
import { AsyncPipe, CurrencyPipe } from '@angular/common';

@Component({
    selector: 'app-dealer-seller',
    templateUrl: './dealer-seller.component.html',
    styleUrls: ['./dealer-seller.component.scss'],
    imports: [TranslocoDirective, LegacyTableModule, FormsModule, ReactiveFormsModule, MatSlideToggleModule, AsyncPipe, CurrencyPipe]
})
export class DealerSellerComponent implements OnInit, OnDestroy {
  @Select(ConfigurationState.configuration) private readonly configuration$: Observable<Configuration>;
  @Select(tenantClass) tenantClass$: Observable<string>;
  tenantName$: Observable<string>;
  dealerSellerForm: UntypedFormGroup;
  @ViewChild('sellerDrawer') sellerDrawer: TableRowSelectorDirective;
  @ViewChild('pmAuctionToggle') pmAuctionToggle: MatSlideToggle;
  @ViewChild('offersToggle') offersToggle: MatSlideToggle;
  @ViewChild('questionsToggle') questionsToggle: MatSlideToggle;

  private readonly destroyed$ = new Subject();

  financeSources: FinanceSource[] = [];
  feeTypes: FeeType[] = [];
  organization: Organization;
  organizationProfile: OrganizationProfile;
  isEligibleToPost = false;
  dailyPostingLimit: number | undefined;
  routerState: any;

  postingData: any = {
    postingCriteria: {
      postingEligibility: '',
      dailyPostingLimit: '',
      optInPmAuction: '',
      acceptingOffers: '',
      acceptingQuestions: ''
    }
  };

  postingColumns: TableColumn[] = [
    { key: 'postingEligibility', label: '' },
    { key: 'dailyPostingLimit', label: '' },
    { key: 'optInPmAuction', label: '' },
    { key: 'acceptingOffers', label: '' },
    { key: 'acceptingQuestions', label: '' },
    { key: 'postSettings', label: '' }
  ];

  feeColumns: any = [
    { key: 'feeName', label: '' },
    { key: 'standard', label: '' }
  ];

  cardConfig = {
    options: {
      fullWidth: true,
      showLabel: false
    }
  };

  constructor(
    private store: Store,
    private readonly financeSourceService: FinanceSourceService,
    private analyticsService: AnalyticsService,
    private readonly router: Router,
    private readonly translocoService: TranslocoService,
    private readonly organizationService: OrganizationService
  ) {
    const currentNaviation = this.router.currentNavigation();

    if (currentNaviation && currentNaviation.extras && currentNaviation.extras.state) {
      this.routerState = currentNaviation.extras.state;
    }
  }

  ngOnInit() {
    this.getTranslations();
    this.tenantName$ = this.configuration$.pipe(map((configuration: Configuration) => configuration.tenantName));
    this.dealerSellerForm = new UntypedFormGroup({
      pmToggleFC: new UntypedFormControl(),
      offerToggleFC: new UntypedFormControl(),
      questionToggleFC: new UntypedFormControl()
    });

    this.store
      .select(_state => _state.org.organization)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(organization => {
        this.updateOrganization(organization);
      });

    const params = new HttpParams().append('expandFinanceSources', false);
    this.financeSourceService
      .getFinanceSourceList(params)
      .pipe(take(1), takeUntil(this.destroyed$))
      .subscribe(res => {
        this.financeSources = res.data;
        this.dailyPostingLimit = this.financeSources.find(result => result.dailyPostingLimit !== 0).dailyPostingLimit;

        this.postingData.postingCriteria.dailyPostingLimit = this.dailyPostingLimit ? this.dailyPostingLimit : 0;
      });

    this.analyticsService.trackPageLoad({
      page: {
        pageName: `${this.analyticsService.digitalData.page.category.site}:Profile Settings:Dealership:Seller`,
        section: 'Profile Settings',
        subsection: 'Dealership',
        title: 'Seller'
      }
    });
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  updateOrganization(organization: Organization) {
    this.organization = organization;
    this.organizationProfile = organization.organizationProfile;
    this.isEligibleToPost = organization.organizationProfile.eligibleToSell;
    this.feeTypes = organization.organizationFees.feeTypes;
    if(organization.organizationFees.feeTypes){
      this.feeTypes = [];
      organization.organizationFees.feeTypes.forEach(feeTypeItem =>{
        if(feeTypeItem.feeType != 'Customer Monthly View Fee'){
          this.feeTypes.push(feeTypeItem);
        }
      });
    }
    this.populateOrganizationProfileData();
  }

  populateOrganizationProfileData() {
    this.postingData.postingCriteria.postingEligibility = this.organizationProfile.eligibleToSell ? 'Eligible' : '';
    this.postingData.postingCriteria.optInPmAuction = this.organizationProfile.optInPmAuction ? 'Yes' : 'No';
    this.postingData.postingCriteria.acceptingOffers = this.organizationProfile.acceptingOffers ? 'Yes' : 'No';
    this.postingData.postingCriteria.acceptingQuestions = this.organizationProfile.acceptingQuestions ? 'Yes' : 'No';
  }

  getTranslations() {
    this.translocoService
      .selectTranslateObject('profileManagement.dealerSeller.postTableHeader')
      .pipe(takeUntil(this.destroyed$))
      .subscribe(translate => {
        this.postingColumns[0].label = translate.labelPostingEligibility;
        this.postingColumns[1].label = translate.labelDailyPostingLimit;
        this.postingColumns[2].label = translate.labelAcceptingPMAuction;
        this.postingColumns[3].label = translate.labelAcceptingOffers;
        this.postingColumns[4].label = translate.labelAcceptingQuestions;
        this.postingColumns[5].label = translate.labelPostSettings;
      });
    this.translocoService
      .selectTranslateObject('profileManagement.dealerSeller.feeTypesTableHeader')
      .pipe(takeUntil(this.destroyed$))
      .subscribe(translate => {
        this.feeColumns[0].label = translate.labelFeeName;
        this.feeColumns[1].label = translate.labelFeeStandard;
      });
  }

  updateToggle(event: MatSlideToggleChange, toggle: string) {
    this.organizationProfile[toggle] = event.checked;
    this.organizationService.updateOrganization(this.organization.oeUuid, this.organization)
      .pipe(take(1))
      .subscribe(organization => {
        this.updateOrganization(organization);  // update it in this component
        this.store.dispatch(new SaveOrganization(organization));  // and in the rxjs store
      });
    this.populateOrganizationProfileData();  // and on the page
  }

  updateForm() {
    this.focusElement('seller_header');
    this.setupToggles();
  }

  setupToggles() {
    setTimeout(() => {
      this.pmAuctionToggle.checked = this.organizationProfile.optInPmAuction;
      this.offersToggle.checked = this.organizationProfile.acceptingOffers;
      this.questionsToggle.checked = this.organizationProfile.acceptingQuestions;
    }, 0);
  }

  focusElement(id) {
    setTimeout(() => {
      const element = document.getElementById(id);
      if (element) {
        element.focus();
      }
    }, 0);
  }
}
